import * as React from "react";
import { clsx } from "clsx";
import { ToggleReact } from "common/components/toggle";
import { ToggleTheme } from "common/components/toggle/types";
import { ButtonSize, ButtonTheme, ButtonType } from "common/components/button/constants";
import { ButtonReact } from "common/components/button";
import { SelectReact } from "common/components/select";
import { SlackChannelSelectorReact } from "common/widgets/slack-channel-selector";
import { InputReact } from "common/components/input";
import { UserIconReact } from "common/widgets/user-icon";
import { DropMenuReact } from "common/components/drop-menu";
import { ItemsMenuReact } from "common/components/items-menu";
import { SpinnerReact } from "common/components/spinner";
import { SIZE, TextHighlightReact } from "common/components/text-highlight";
import { SelectTheme } from "common/components/select/constants";
import { getFormattedUserName } from "common/widgets/sharing/modal";
import { addExternalUserFn } from "common/query-builder/query-report-header/subscribe-to-boards-menu/util";
import { IconSizeType } from "common/constants";
import sharingModalStyles from "common/widgets/sharing/modal/index.module.styl";
import { useTranslation } from "common/i18n/hooks";
import styles from "./index.module.styl";
import { DIGEST_RECURRENCE_FRIENDLY_NAME, DigestDateOrdinal, DigestRecurrence, DigestType, LOADING_STATES, SUBSCRIBE_WEEK_DAY_LABEL, SUBSCRIBE_WEEK_DAYS, SubscribeSendMethod, SubscribeSendMethodLabel } from "./types";
import { DIGEST_COPY } from "./constants";
const FrequencySelector = (props) => {
  const {
    print
  } = useTranslation();
  const {
    recurrence,
    selectedTimezone,
    monthlyDateOrdinal,
    startDate
  } = props.cmp.state;
  return /* @__PURE__ */ React.createElement("div", { "data-sentry-component": "FrequencySelector", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement("div", { className: styles.buttonContainer }, [DigestRecurrence.Daily, DigestRecurrence.Weekly, DigestRecurrence.Monthly].map((option) => /* @__PURE__ */ React.createElement(ButtonReact, { className: clsx(styles.button, {
    [styles.selected]: recurrence === option
  }), key: option, theme: ButtonTheme.Tertiary, onClick: () => props.cmp.onSelectFrequency(option) }, print(DIGEST_RECURRENCE_FRIENDLY_NAME[option])))), /* @__PURE__ */ React.createElement("div", { className: clsx(styles.frequencySelectContainer, {
    [styles.dailyLayout]: recurrence === DigestRecurrence.Daily,
    [styles.monthlyLayout]: recurrence === DigestRecurrence.Monthly,
    [styles.weeklyLayout]: recurrence === DigestRecurrence.Weekly
  }) }, recurrence === DigestRecurrence.Monthly && /* @__PURE__ */ React.createElement(SelectReact, { key: "ordinal", selectedOption: props.cmp.getMonthlyOrdinalName(monthlyDateOrdinal), onChange: (_ref) => {
    let {
      detail
    } = _ref;
    return props.cmp.updateMonthlyDateOrdinal(detail.item.value);
  }, sections: [{
    items: [DigestDateOrdinal.First, DigestDateOrdinal.Last].map((ordinal) => ({
      label: print(props.cmp.getMonthlyOrdinalName(ordinal)),
      value: ordinal
    }))
  }] }), [DigestRecurrence.Weekly, DigestRecurrence.Monthly].includes(recurrence) && /* @__PURE__ */ React.createElement(SelectReact, { key: "day", selectedOption: print(SUBSCRIBE_WEEK_DAY_LABEL[startDate.format("dddd")]), onChange: (_ref2) => {
    let {
      detail
    } = _ref2;
    return props.cmp.updateSelectedWeeklyDay(detail.item.value);
  }, sections: [{
    items: SUBSCRIBE_WEEK_DAYS.map((day) => ({
      label: SUBSCRIBE_WEEK_DAY_LABEL[day],
      value: day
    }))
  }] }), /* @__PURE__ */ React.createElement(SelectReact, { key: "hour", selectedOption: startDate.format("h A"), onChange: (_ref3) => {
    let {
      detail
    } = _ref3;
    return props.cmp.updateHourOfDay(detail.item.value);
  }, sections: [{
    items: props.cmp.hoursOfTheDay.map((label, index) => ({
      label,
      value: index
    }))
  }], "data-sentry-element": "SelectReact", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ React.createElement(SelectReact, { className: styles.timezoneSelect, key: "timezone", menuWidth: 280, selectedOption: selectedTimezone, onChange: (_ref4) => {
    let {
      detail
    } = _ref4;
    return props.cmp.updateTimezone(detail.item.value);
  }, sections: [{
    items: props.cmp.timezones.map((label) => ({
      label,
      value: label
    }))
  }], "data-sentry-element": "SelectReact", "data-sentry-source-file": "template.tsx" })));
};
const SubscribeButton = (props) => {
  const {
    t
  } = useTranslation();
  const isSaving = props.cmp.state.isSavingDigest;
  let disabled = props.disabled;
  if (isSaving) {
    disabled = true;
  }
  return /* @__PURE__ */ React.createElement(ButtonReact, { className: styles.subscribeButton, disabled, styleOverride: "a.mp-button-container .mp-button-content {\n            flex: none; width: auto;\n          }", theme: ButtonTheme.Primary, onClick: () => props.cmp.createDigest(), ...isSaving ? {
    icon: "spinner"
  } : {}, "data-sentry-element": "ButtonReact", "data-sentry-component": "SubscribeButton", "data-sentry-source-file": "template.tsx" }, t("subscriptions.subscribe"));
};
const UpdateView = (props) => {
  const {
    t
  } = useTranslation();
  const {
    isDeletingDigest,
    isSavingDigest
  } = props.cmp.state;
  return /* @__PURE__ */ React.createElement("div", { className: styles.digestListActions, "data-sentry-component": "UpdateView", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement(ButtonReact, { className: styles.button, disabled: props.cmp.editButtonsDisabled, onClick: () => props.cmp.onDeleteDigest(props.cmp.state.currentDigest.id), type: ButtonType.Destructive, ...isDeletingDigest ? {
    icon: "spinner"
  } : {}, "data-sentry-element": "ButtonReact", "data-sentry-source-file": "template.tsx" }, t("common.delete")), /* @__PURE__ */ React.createElement(ButtonReact, { className: styles.button, disabled: props.cmp.editButtonsDisabled, onClick: () => props.cmp.patchDigestFromEditScreen(props.cmp.state.currentDigest.id), theme: ButtonTheme.Primary, ...isSavingDigest ? {
    icon: "spinner"
  } : {}, "data-sentry-element": "ButtonReact", "data-sentry-source-file": "template.tsx" }, t("common.update")));
};
const SubscribeToExistingDigestButton = (props) => {
  const {
    t
  } = useTranslation();
  const currentUserSubbed = props.cmp.currentUserIsAlreadySubscribedToDigest(props.digest.id);
  const updatingCurrentDigest = props.cmp.state.digestIsBeingUpdatedMap.get(props.digest.id);
  let icon = "feed";
  if (updatingCurrentDigest) {
    icon = "spinner";
  } else if (currentUserSubbed) {
    icon = "checkmark";
  }
  return /* @__PURE__ */ React.createElement(ButtonReact, { icon, size: ButtonSize.Small, theme: ButtonTheme.Secondary, onClick: (event) => {
    event.stopPropagation();
    props.cmp.onSubscribeToExistingDigest(props.digest, currentUserSubbed);
  }, "data-sentry-element": "ButtonReact", "data-sentry-component": "SubscribeToExistingDigestButton", "data-sentry-source-file": "template.tsx" }, currentUserSubbed ? t("subscriptions.subscribed") : t("subscriptions.subscribe"));
};
const DashboardSelector = (props) => {
  var _props$cmp$state$sele;
  if (props.cmp.props.digestToUpdate) {
    return null;
  }
  return /* @__PURE__ */ React.createElement("div", { "data-sentry-component": "DashboardSelector", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement("div", { className: styles.dashboardSelectContainer }, /* @__PURE__ */ React.createElement(SelectReact, { className: styles.dashboardSelect, icon: {
    leftIcon: "icon-grid"
  }, sections: [props.cmp.getDashboardSections()], selectedOption: ((_props$cmp$state$sele = props.cmp.state.selectedDashboard) === null || _props$cmp$state$sele === void 0 ? void 0 : _props$cmp$state$sele.title) || "Select Board", theme: SelectTheme.Secondary, onChange: (_ref5) => {
    let {
      detail
    } = _ref5;
    return props.cmp.onClickDashboard(detail.item.value);
  }, "data-sentry-element": "SelectReact", "data-sentry-source-file": "template.tsx" })));
};
const DetailView = (props) => {
  const {
    currentTab,
    isSendingTestDigest
  } = props.cmp.state;
  const {
    t,
    print
  } = useTranslation();
  return /* @__PURE__ */ React.createElement("div", { "data-sentry-component": "DetailView", "data-sentry-source-file": "template.tsx" }, !props.cmp.props.dashboardId ? /* @__PURE__ */ React.createElement(DashboardSelector, { cmp: props.cmp }) : null, /* @__PURE__ */ React.createElement("div", { className: styles.buttonContainer }, [SubscribeSendMethod.Email, SubscribeSendMethod.Slack].map((option) => /* @__PURE__ */ React.createElement(ButtonReact, { className: clsx(styles.button, {
    [styles.selected]: currentTab === option
  }), key: option, theme: ButtonTheme.Tertiary, onClick: () => props.cmp.updateCurrentTab(option) }, print(SubscribeSendMethodLabel[option])))), /* @__PURE__ */ React.createElement(FrequencySelector, { cmp: props.cmp, "data-sentry-element": "FrequencySelector", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ React.createElement("div", { className: styles.subscriberSection }, /* @__PURE__ */ React.createElement("div", { className: styles.subscriberTitle }, /* @__PURE__ */ React.createElement("span", { className: styles.subscriberCount }, "".concat(t("subscriptions.subscribers"), " (").concat(props.subscribersCount, ")")), /* @__PURE__ */ React.createElement(ButtonReact, { disabled: props.disableSendOrSubscribe || props.cmp.editButtonsDisabled, theme: ButtonTheme.Tertiary, onClick: () => props.cmp.onSendTest(), ...isSendingTestDigest ? {
    icon: "spinner"
  } : {}, "data-sentry-element": "ButtonReact", "data-sentry-source-file": "template.tsx" }, t("subscriptions.send-test"))), props.children), props.cmp.state.currentDigest ? /* @__PURE__ */ React.createElement(UpdateView, { cmp: props.cmp }) : /* @__PURE__ */ React.createElement(SubscribeButton, { cmp: props.cmp, disabled: props.disableSendOrSubscribe }));
};
const SlackView = (props) => {
  const {
    isEditDisabled,
    slackSubscriptions
  } = props.cmp.state;
  const disableSendOrSubscribe = !slackSubscriptions.length || !props.cmp.props.dashboardId && !props.cmp.state.selectedDashboard;
  return /* @__PURE__ */ React.createElement(DetailView, { cmp: props.cmp, disableSendOrSubscribe, subscribersCount: slackSubscriptions.length, "data-sentry-element": "DetailView", "data-sentry-component": "SlackView", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement(SlackChannelSelectorReact, { buttonFullWidth: true, buttonTheme: ButtonTheme.Primary, className: styles.connectSlackButton, isReadonly: isEditDisabled, selectedChannels: slackSubscriptions.map((subscription) => {
    return {
      name: subscription.slack_channel_name,
      id: subscription.slack_channel_id
    };
  }), onSelectedChannelsChanged: (event) => props.cmp.onSlackSubscriptionsChanged(event), "data-sentry-element": "SlackChannelSelectorReact", "data-sentry-source-file": "template.tsx" }));
};
export const EmailRecipients = (props) => {
  const {
    print
  } = useTranslation();
  return /* @__PURE__ */ React.createElement("div", { className: clsx(sharingModalStyles.shareSectionWrapper), "data-sentry-component": "EmailRecipients", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement("div", { className: clsx(styles.shareSection, sharingModalStyles.shareSection) }, /* @__PURE__ */ React.createElement("ul", { className: clsx(sharingModalStyles.shareList), role: "list" }, props.emailSubscribers.map((pm) => props.membersMap.get(pm.email) || pm).map((member, index) => {
    const {
      id,
      name,
      email
    } = member;
    return /* @__PURE__ */ React.createElement("li", { className: clsx(sharingModalStyles.shareListItem), key: email, role: "listitem" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(UserIconReact, { className: clsx(sharingModalStyles.mpUserIcon), disableTooltip: true, email, id, size: IconSizeType.Small, ...{
      name: name || email
    } }))), /* @__PURE__ */ React.createElement("div", { className: clsx(sharingModalStyles.display) }, /* @__PURE__ */ React.createElement("div", { className: clsx(sharingModalStyles.nameWrapper) }, /* @__PURE__ */ React.createElement("div", { className: clsx(sharingModalStyles.name) }, print(getFormattedUserName(name, email)))), /* @__PURE__ */ React.createElement("div", { className: clsx(sharingModalStyles.subtitle) }, email)), /* @__PURE__ */ React.createElement("div", { className: clsx(sharingModalStyles.controls) }, /* @__PURE__ */ React.createElement(ButtonReact, { className: styles.removeButton, icon: "trashcan", theme: ButtonTheme.Tertiary, onClick: () => props.removeFunc(index) })));
  }))));
};
const EmailView = (props) => {
  var _props$cmp$searchInpu;
  const {
    emailSubscribers,
    subscriberSearch
  } = props.cmp.state;
  const {
    t,
    print
  } = useTranslation();
  const disableSendOrSubscribe = !emailSubscribers.length || Boolean(props.cmp.updateSubscriberSearchError) || !props.cmp.props.dashboardId && !props.cmp.state.selectedDashboard;
  return /* @__PURE__ */ React.createElement(DetailView, { cmp: props.cmp, disableSendOrSubscribe, subscribersCount: emailSubscribers.length, "data-sentry-element": "DetailView", "data-sentry-component": "EmailView", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(InputReact, { errorMessage: props.cmp.updateSubscriberSearchError, ref: props.cmp.searchInputElementRef, placeholder: t("subscriptions.search-placeholder"), validationStatus: props.cmp.updateSubscriberSearchError ? "invalid" : "", onFocus: () => props.cmp.onFocus(), onBlur: () => props.cmp.onBlur(), onInput: (_ref6) => {
    let {
      target
    } = _ref6;
    return props.cmp.updateSubscriberSearch(target.value);
  }, onKeydown: (ev) => props.cmp.onSubscriberSearchKeydown(ev), value: subscriberSearch, "data-sentry-element": "InputReact", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ React.createElement(DropMenuReact, { open: !!subscriberSearch && props.cmp.state.subscriberSearchFocused, "data-sentry-element": "DropMenuReact", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement(
    ItemsMenuReact,
    {
      className: styles.memberDropdown,
      emptySearchResultsMessage: print(DIGEST_COPY.emailNoResults),
      keydownProxyEl: (_props$cmp$searchInpu = props.cmp.searchInputElementRef) === null || _props$cmp$searchInpu === void 0 ? void 0 : _props$cmp$searchInpu.current,
      searchFilter: subscriberSearch,
      sections: [props.cmp.getSections()],
      sectionsFromInputFn: (input) => addExternalUserFn(input, props.cmp.state.emailDomains),
      onClickItem: (ev) => props.cmp.onClickMember(ev.detail.item.value),
      onFocusItem: () => props.cmp.onFocus(),
      "data-sentry-element": "ItemsMenuReact",
      "data-sentry-source-file": "template.tsx"
    }
  ))), emailSubscribers.length ? /* @__PURE__ */ React.createElement(EmailRecipients, { emailSubscribers, membersMap: props.cmp.state.membersMap, removeFunc: (index) => props.cmp.removeSubscriber(index) }) : /* @__PURE__ */ React.createElement("div", null));
};
const LoadingMessage = () => {
  const {
    t
  } = useTranslation();
  return /* @__PURE__ */ React.createElement("div", { className: styles.loadingMessage, "data-sentry-component": "LoadingMessage", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement(SpinnerReact, { "data-sentry-element": "SpinnerReact", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ React.createElement("span", null, t("subscriptions.loading-placeholder")));
};
const DigestItemSubscriberSummary = (props) => {
  const {
    t
  } = useTranslation();
  const isEmail = props.digestType === DigestType.Email;
  if (isEmail) {
    const recipientCount = props.digest.recipients.length;
    return /* @__PURE__ */ React.createElement("span", null, t("subscriptions.count-subscribers", {
      count: recipientCount
    }));
  } else {
    return /* @__PURE__ */ React.createElement("span", null, props.digest.slack_subscriptions.map((slackChannel) => {
      return /* @__PURE__ */ React.createElement("span", { key: slackChannel.slack_channel_id }, "#", slackChannel.slack_channel_name, "\xA0");
    }));
  }
};
export const ListView = (props) => {
  const {
    availableMembers,
    currentTab,
    dashboardDigests
  } = props.cmp.state;
  const digestType = currentTab === SubscribeSendMethod.Slack ? DigestType.Slack : DigestType.Email;
  const digestsList = digestType === DigestType.Email ? props.cmp.emailDigests : props.cmp.slackDigests;
  const hasDigests = Boolean(digestsList.length);
  const {
    t,
    print
  } = useTranslation();
  const noDigestsMessage = currentTab === SubscribeSendMethod.Slack ? t("subscriptions.no-slack-subscriptions") : t("subscriptions.no-email-subscriptions");
  const dataIsLoading = !props.cmp.currentUserEmail || LOADING_STATES.includes(availableMembers.loading) || !props.cmp.props.createOnly && LOADING_STATES.includes(dashboardDigests.loading);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: clsx(styles.subscribeToBoardsNav, styles.listView) }, /* @__PURE__ */ React.createElement("div", { className: styles.subscribeToBoardsToggleContainer }, /* @__PURE__ */ React.createElement(ToggleReact, { className: styles.subscribeToBoardsToggle, options: [{
    icon: "email",
    label: "".concat(print(SubscribeSendMethodLabel[SubscribeSendMethod.Email]), " ").concat(props.cmp.emailDigestsCountString),
    value: SubscribeSendMethod.Email
  }, {
    icon: "slack-logo",
    label: "".concat(print(SubscribeSendMethodLabel[SubscribeSendMethod.Slack]), " ").concat(props.cmp.slackDigestsCountString),
    value: SubscribeSendMethod.Slack
  }], selected: currentTab, theme: ToggleTheme.TabBar, onSelect: (_ref7) => {
    let {
      detail
    } = _ref7;
    return props.cmp.updateCurrentTab(detail.selected);
  }, "data-sentry-element": "ToggleReact", "data-sentry-source-file": "template.tsx" }))), /* @__PURE__ */ React.createElement("div", null, dataIsLoading ? /* @__PURE__ */ React.createElement(LoadingMessage, null) : /* @__PURE__ */ React.createElement("div", { className: clsx({
    [styles.hasEntries]: hasDigests
  }) }, !hasDigests ? /* @__PURE__ */ React.createElement("div", { className: styles.noDigestsMessage }, noDigestsMessage) : /* @__PURE__ */ React.createElement("div", null), /* @__PURE__ */ React.createElement("div", { className: styles.digestList }, /* @__PURE__ */ React.createElement("div", { role: "list" }, digestsList.map((digest) => {
    return /* @__PURE__ */ React.createElement("div", { className: clsx(styles.digestEntry, {
      [styles.clickable]: props.cmp.canEditDigest(digest)
    }), key: digest.id, onClick: () => props.cmp.canEditDigest(digest) ? props.cmp.onSelectDigest(digest) : {}, role: "listitem" }, /* @__PURE__ */ React.createElement("div", { className: styles.digestHeader }, /* @__PURE__ */ React.createElement("span", null, digest.schedule), digestType === DigestType.Email ? /* @__PURE__ */ React.createElement(SubscribeToExistingDigestButton, { cmp: props.cmp, digest }) : /* @__PURE__ */ React.createElement("span", null)), /* @__PURE__ */ React.createElement("div", { className: styles.digestBody }, digest.timezone), /* @__PURE__ */ React.createElement("div", { className: styles.digestFooter }, props.cmp.currentUserIsOwnerOfDigest(digest) ? /* @__PURE__ */ React.createElement(TextHighlightReact, { className: styles.textHighlight, size: SIZE.SMALL, text: "Owner" }) : /* @__PURE__ */ React.createElement("span", null), /* @__PURE__ */ React.createElement(DigestItemSubscriberSummary, { digest, digestType })));
  }))), /* @__PURE__ */ React.createElement("div", { className: styles.digestListActions }, /* @__PURE__ */ React.createElement(ButtonReact, { className: styles.button, href: props.cmp.manageDigestsModeUrl, icon: "externalLink", theme: ButtonTheme.Secondary }, t("subscriptions.view-all")), /* @__PURE__ */ React.createElement(ButtonReact, { className: styles.button, theme: ButtonTheme.Primary, onClick: () => props.cmp.onCreateNewDigest() }, t("subscriptions.create-new"))))));
};
export const CreateOrEditView = (props) => {
  const {
    currentDigest,
    currentTab
  } = props.cmp.state;
  const {
    t
  } = useTranslation();
  const editing = Boolean(currentDigest);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: styles.subscribeToBoardsNav }, /* @__PURE__ */ React.createElement(ButtonReact, { className: styles.subscribeToBoardsNavBackButton, icon: "arrow-left", theme: ButtonTheme.Tertiary, onClick: () => props.cmp.onNavBackButton(), "data-sentry-element": "ButtonReact", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ React.createElement("div", { className: styles.subscribeToBoardsNavTitle }, editing ? t("subscriptions.edit-title") : t("subscriptions.create-title"))), /* @__PURE__ */ React.createElement("div", { className: styles.subscribeToBoardsContent }, currentTab === SubscribeSendMethod.Slack && /* @__PURE__ */ React.createElement(SlackView, { cmp: props.cmp }), currentTab === SubscribeSendMethod.Email && /* @__PURE__ */ React.createElement(EmailView, { cmp: props.cmp })));
};
